/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Autocomplete, Box, Grid, MenuItem, TextField,
} from '@mui/material';
import { ChipOption, CountryOption, DebouncedSearchInput } from '@marageti/z4-lib';
import { Option, AssigneeOption } from '../options';

type InProgressFiltersProps = {
  onSearchChange: (v: string) => void;
  searchValue: string;
  orderBy: string;
  orderByOptions: Option[];
  sortBy: string;
  sortByOptions: Option[];
  onOrderSortChange: (n: string, v: string) => void;
  isLoadingDestinations: boolean;
  selectedDestinations: CountryOption[];
  destinationOptions: CountryOption[];
  onDestinationChange: (countries: CountryOption[]) => void;
  assignees: AssigneeOption[];
  selectedAssignee: AssigneeOption;
  onAssigneeChange: (assignee: AssigneeOption | null) => void;
  onTagChange: (selectedTag: string) => void;
  tagOptions: ChipOption[];
  selectedTag: string;
};

const InProgressFilters = ({
  // Search Props
  searchValue,
  onSearchChange,
  // Destination Props
  isLoadingDestinations,
  selectedDestinations,
  destinationOptions,
  onDestinationChange,
  // Assignee Props
  assignees,
  selectedAssignee,
  onAssigneeChange,
  // Sort and Order Props
  orderBy,
  orderByOptions,
  sortBy,
  sortByOptions,
  onOrderSortChange,
  // Tag Props
  onTagChange,
  tagOptions,
  selectedTag,
}: InProgressFiltersProps) => (
  <Box sx={{ mb: 4 }}>
    <Grid container columnSpacing={2}>
      <Grid item mobile={12} desktop={2}>
        <DebouncedSearchInput
          fullWidth
          key="fullText"
          label="Search"
          name="fullText"
          onChange={onSearchChange}
          defaultValue={searchValue}
        />
      </Grid>
      <Grid item mobile={12} desktop={2}>
        <Autocomplete
          getOptionLabel={(o) => o.label}
          id="primaryDestination"
          isOptionEqualToValue={(o, v) => o.value === v.value}
          loading={isLoadingDestinations}
          onChange={(e, v) => onDestinationChange(v)}
          options={destinationOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Destinations"
              name="primaryDestination"
            />
          )}
          ChipProps={{
            size: 'small',
            sx: {
              backgroundColor: 'secondary.light',
            },
          }}
          fullWidth
          multiple
          value={selectedDestinations}
        />
      </Grid>
      <Grid item mobile={12} desktop={2}>
        <TextField
          fullWidth
          disabled={searchValue !== ''}
          key="tags-select"
          label="Tags"
          name="tag"
          onChange={(e) => onTagChange(e.target.value)}
          select
          value={selectedTag}
        >
          {tagOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item mobile={12} desktop={2}>
        <Autocomplete
          getOptionKey={(o) => o.value}
          getOptionLabel={(o) => o.title}
          groupBy={(o) => o.status || ''}
          id="assignee"
          isOptionEqualToValue={(o, v) => o.value === v.value}
          onChange={(_, v) => onAssigneeChange(v)}
          options={assignees}
          renderInput={(params) => (
            <TextField {...params} label="Assignee" name="assignee" />
          )}
          value={selectedAssignee}
        />
      </Grid>
      <Grid item mobile={6} desktop={2}>
        <TextField
          fullWidth
          disabled={searchValue !== ''}
          key="sort-by-select"
          label="Sort By"
          name="sort"
          onChange={(e) => onOrderSortChange('sort', e.target.value)}
          select
          value={sortBy}
        >
          {sortByOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.title}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item mobile={6} desktop={2}>
        <TextField
          fullWidth
          select
          disabled={searchValue !== ''}
          key="order-by-select"
          label="Order By"
          name="order"
          onChange={(e) => onOrderSortChange('order', e.target.value)}
          value={orderBy}
        >
          {orderByOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.title}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  </Box>
);

export default InProgressFilters;
